import React from "react";
import { Link } from "react-router-dom";
import eruptionLogo from "../../assets/logos/logo.png";
import { useAnalyticsConsent } from "../../contexts/AnalyticsConsentContext";
import OptimizedContentImage from "../common/OptimizedContentImage";

const currentYear = new Date().getFullYear();

const isNewsletterEnabled =
  (process.env.REACT_APP_NEWSLETTER_ENABLED ?? "")
    .toString()
    .trim()
    .toLowerCase() === "true";

const footerNav = [
  { label: "Events", to: "/events" },
  { label: "Mixes", to: "/mixes" },
  ...(isNewsletterEnabled ? [{ label: "Newsletter", to: "/newsletter" }] : []),
  { label: "Privacy Policy", to: "/privacy" },
  { label: "Terms & Conditions", to: "/terms" },
  { label: "Legal Notice", to: "/legal" },
  { label: "Contact", to: "/contact" },
  { label: "Advertise", to: "/advertise" },
  { label: "Report A Problem", href: "https://forms.gle/1aRFbcVSU6LW2gaA6" },
];

const socialLinks = [
  {
    label: "Instagram",
    href: "https://www.instagram.com/eruptionradiouk",
  },
  {
    label: "X",
    href: "https://www.x.com/eruptionradiouk",
  },
  {
    label: "Facebook",
    href: "https://www.facebook.com/eruptionradiouk",
  },
  {
    label: "TikTok",
    href: "https://www.tiktok.com/@eruptionradiouk",
  },
  {
    label: "Mixcloud",
    href: "https://www.mixcloud.com/EruptionRadioUK",
  },
];

const SiteFooter: React.FC = () => {
  const { canManageConsent, openConsentPrompt } = useAnalyticsConsent();

  return (
    <footer
      className="border-t border-gray-800 bg-gray-950/60"
      role="contentinfo"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-32 sm:pb-36">
        <div className="grid gap-10 md:grid-cols-[1.2fr_1fr_1fr]">
          <div className="space-y-4">
            <Link to="/" className="inline-flex items-start gap-3">
              <OptimizedContentImage
                src={eruptionLogo}
                alt="Eruption Radio logo"
                className="h-10 w-auto"
                fit="contain"
                imageClassName="h-full w-auto"
                sizes="64px"
                priority
                preload
              />
              <span className="text-gray-200 font-semibold tracking-wide uppercase text-sm">
                Eruption Radio
              </span>
            </Link>
            <p className="text-slate-200 text-sm leading-relaxed max-w-md">
              Broadcasting the best in underground music from London and beyond.
              Tune in live 24/7 or catch-up on demand.
            </p>
            <p className="text-slate-300 text-xs">
              © {currentYear} Eruption Radio Ltd. All rights reserved.
            </p>
          </div>

          <div>
            <h3 className="text-gray-200 text-sm font-semibold uppercase tracking-wide">
              Links
            </h3>
            <nav className="mt-4 grid gap-2 text-sm">
              {footerNav.map((item) =>
                item.href ? (
                  <a
                    key={item.label}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-slate-300 hover:text-white transition-colors"
                  >
                    {item.label}
                  </a>
                ) : (
                  <Link
                    key={item.label}
                    to={item.to as string}
                    className="text-slate-300 hover:text-white transition-colors"
                  >
                    {item.label}
                  </Link>
                )
              )}
              {canManageConsent ? (
                <button
                  type="button"
                  onClick={openConsentPrompt}
                  className="text-left text-slate-300 hover:text-white transition-colors"
                >
                  Cookie settings
                </button>
              ) : null}
            </nav>
          </div>

          <div>
            <h3 className="text-gray-200 text-sm font-semibold uppercase tracking-wide">
              Stay connected
            </h3>
            <ul className="mt-4 space-y-2 text-sm">
              <li>
                <a
                  href="mailto:info@eruptionradio.uk"
                  className="text-slate-300 hover:text-white transition-colors"
                >
                  info@eruptionradio.uk
                </a>
              </li>
              {socialLinks.map((social) => (
                <li key={social.label}>
                  <a
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-slate-300 hover:text-white transition-colors"
                  >
                    {social.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default SiteFooter;
